import React from 'react';
import {Grid, Typography} from '@mui/material';
import {useMutation} from '@apollo/client';
import {Prompt} from '../../components/Prompt/Prompt';
import AutoField, {yesNoField} from '../../components/AutoForm/AutoFieldNew';
import {RiskNotesContextProps} from './riskNotesFields';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {UPDATE_CONTACT} from '../../components/AccountOverview/accountOverviewQueries';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {useOnUpdate} from '../../hooks/useOnUpdate';
import validateUUID from '../../utils/validateUUID';

export const RiskNotesComplianceContact = ({
  risk,
  contactId,
}: RiskNotesContextProps & {contactId: string}) => {
  const contact = risk.reviewDetails?.contacts?.find((c) => c.id === contactId);
  const [{value: giaValue}, {error: giaError}, {setValue: setNoteValue}] =
    useAutoField<boolean>(`giaHasLiquidityNeeds.${contactId}`);
  const [{value: pensionValue}, {error: pensionError}, {setValue: setScore}] =
    useAutoField<boolean>(`pensionHasLiquidityNeeds.${contactId}`);

  const [updateContactMutation] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  useOnUpdate(() => {
    updateContactMutation({
      variables: {
        contactId: validateUUID(contactId)!,
        giaHasLiquidityNeeds: giaValue,
        pensionHasLiquidityNeeds: pensionValue,
      },
    });
  }, [giaValue, pensionValue]);

  if (!contact) {
    return (
      <Prompt title="Error loading contact" content="Contact not found" variant="error" />
    );
  }
  return (
    <>
      <Grid container mb={4} spacing={4}>
        <Grid item xs={5}>
          <Typography variant="h5">
            Does {contact.simplifiedName} have GIA liquidity needs?
          </Typography>
          <AutoField
            field={{
              ...yesNoField,
              label: `${contact.simplifiedName} has GIA liquidity needs`,
              id: `giaHasLiquidityNeeds.${contactId}`,
              initialValue: contact.giaHasLiquidityNeeds ?? undefined,
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <Typography>
            Private assets can take longer to sell than listed stocks. Clients should only
            hold these assets if they are unlikely to need access to a significant
            proportion of the money at short notice. This should be assessed after taking
            into consideration their emergency funds and other money that remains
            accessible. A significant proportion is considered to be 25% or more and short
            notice is defined as 4 months from request to payment.
          </Typography>
        </Grid>
      </Grid>

      <Grid container mb={4} spacing={4}>
        <Grid item xs={5}>
          <Typography variant="h5">
            Does {contact.simplifiedName} have pension liquidity needs?
          </Typography>
          <AutoField
            field={{
              ...yesNoField,
              label: `${contact.simplifiedName} has pension liquidity needs`,
              id: `pensionHasLiquidityNeeds.${contactId}`,
              initialValue: contact.pensionHasLiquidityNeeds ?? undefined,
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <Typography>
            Private assets can take longer to sell than listed stocks. Clients should only
            hold these assets if they are unlikely to need access to a significant
            proportion of the money at short notice. This should be assessed after taking
            into consideration their emergency funds and other money that remains
            accessible. A significant proportion is considered to be 25% or more and short
            notice is defined as 4 months from request to payment.
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};
